import { AxiosResponse } from "axios";
import { apiErrorWithMessages } from "../lib/apiError";
import { Case, CaseWithStatistics } from "../models/Case";
import { CaseFile } from "../models/CaseFile";
import { Paging } from "../models/Paging";
import useApi from "../services/ApiService";
import { MigrationFlagStatus } from "../models/Flags/MigrationFlagStatus";
import { ConflictGroupDto, ConflictResolutionItemDto } from "../models/ConflictGroup";
import { ConflictReasonCount } from "../models/ConflictReasonCount";
import { CaseFileFilters } from "../models/CaseFileFilters";
import {QUERY_CONFIG} from "../constants/QueryConfig";
import {EmptyFolder} from "../models/EmptyFolder";
import { ConflictReason } from "../models/ConflictReason";

export interface GetCasesQueryOptions {
  organizationId?: string;
  status?: string[] | null;
  isLocked?: boolean | null;
  hasFlaggedPaths?: boolean | null;
  hasConflicts?: boolean | null;
  /** Only entities whose flag is in one of these states. */
  flagStatus?: MigrationFlagStatus[] | null;
  /** Only cases with an unresolved conflict of one of these types that can still be acted on, read from the files. */
  conflictReasons?: ConflictReason[] | null;
  createdBy?: string[];
  caseNumber?: string[] | null;
  ids?: string[];
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
}
export class GetCaseFilesForCaseQueryOptions {
  status?: string[] | null;
  extension?: string[] | null;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
  nestedFolder1?: string | null;
  nestedFolder2?: string | null;
  filePath?: string | null;
  destinationPath?: string | null;
  minFileSize?: number | null;
  maxFileSize?: number | null;
  type?: number | null;
  failureType?: string | null;
  conflictReason?: string[] | null;
  conflictStatus?: string[] | null;
}

export class GetCaseEmptyFoldersForCaseQueryOptions {
  status?: string[] | null;
  uploadPathFromRoot?: string | null;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
}

export class CasesRepository {
  private api = useApi();

  async getCases(
    options: GetCasesQueryOptions = {}
  ): Promise<{ data: Case[]; paging: Paging | null }> {
    const {
      status = null,
      isLocked = null,
      hasFlaggedPaths = null,
      hasConflicts = null,
      flagStatus = null,
      conflictReasons = null,
      createdBy = null,
      caseNumber = null,
      ids = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
    } = options;

    try {
      const response = await this.api.get("/api/Cases", {
        params: {
          Status: status,
          IsLocked: isLocked,
          HasFlaggedPaths: hasFlaggedPaths,
          HasConflicts: hasConflicts,
          FlagStatus: flagStatus,
          ConflictReason: conflictReasons,
          CreatedBy: createdBy,
          CaseNumber: caseNumber,
          Id: ids,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
        },
        paramsSerializer: {
          indexes: true,
        },
      });

      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader) {
        paging = JSON.parse(paginationHeader || "");
      }

      return { data, paging };
    } catch (error) {
      // Handle error
      throw new Error("Failed to fetch cases");
    }
  }

  async getCase(id: string): Promise<CaseWithStatistics> {
    try {
      const response = await this.api.get(`/api/Cases/${id}`);
      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(`Failed to fetch case with id: ${id}`);
    }
  }

  async getCaseFilesForCase(
    caseId?: string,
    options: GetCaseFilesForCaseQueryOptions = {}
  ): Promise<{ data: CaseFile[]; paging: Paging | null }> {
    const {
      status = null,
      extension = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      nestedFolder1 = null,
      nestedFolder2 = null,
      filePath = null,
      destinationPath = null,
      minFileSize = null,
      maxFileSize = null,
      type = null,
      failureType = null,
      conflictReason = null,
      conflictStatus = null
    } = options;
    try {
      const apiUrl = caseId
        ? `/api/Cases/${caseId}/files`
        : "/api/CaseFiles/files";
      const response = await this.api.get(apiUrl, {
        params: {
          Status: status,
          Extension: extension,
          NestedFolder1: nestedFolder1,
          NestedFolder2: nestedFolder2,
          FilePath: filePath,
          DestinationPath: destinationPath,
          MinFileSize: minFileSize,
          MaxFileSize: maxFileSize,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
          Type: type,
          FailureType: failureType,
          ConflictReason: conflictReason,
          ConflictStatus: conflictStatus,
        },
        paramsSerializer: {
          indexes: true,
        },
        timeout: QUERY_CONFIG.DEFAULT_TIMEOUT,
      });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");
      
      return { data, paging };
    } catch (error) {
      const message = caseId
        ? `Failed to fetch files for case with id: ${caseId}`
        : `Failed to fetch case files`;
      throw new Error(message);
    }
  }

  async getCaseFilesFiltersForCase(
    id: string
  ): Promise<{ data: CaseFileFilters }> {
    try {
      const response = await this.api.get(`/api/Cases/${id}/files/filters`, {
        timeout: QUERY_CONFIG.DEFAULT_TIMEOUT,
      });
      return { data: response.data };
    } catch (error) {
      throw new Error(`Failed to fetch case filters for case with id: ${id}`);
    }
  }

  async getCaseEmptyFoldersForCase(
    caseId?: string,
    options: GetCaseEmptyFoldersForCaseQueryOptions = {}
  ): Promise<{ data: EmptyFolder[]; paging: Paging | null }> {
    const {
      status = null,
      uploadPathFromRoot = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
    } = options;
    try {
      const response = await this.api.get(`/api/Cases/${caseId}/emptyFolders`, {
        params: {
          Status: status,
          UploadPathFromRoot: uploadPathFromRoot,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
        },
        paramsSerializer: {
          indexes: true,
        },
        timeout: QUERY_CONFIG.DEFAULT_TIMEOUT,
      });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");

      return { data, paging };
    } catch (error) {
      throw new Error(`Failed to fetch empty folders for case with id: ${caseId}`);
    }
  }

  async updateCaseFlag(
    caseId: string,
    flagStatus: MigrationFlagStatus
  ): Promise<void> {
    try {
      const data: Record<string, any> = {
        caseId: caseId,
        flagStatus: flagStatus,
      };

      const response: AxiosResponse<void> = await this.api.patch(
        `/api/Cases/updateFlag`,
        data
      );

      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(`Failed to update flag info of case with id: ${caseId}`);
    }
  }

  async updateCaseBlock(caseId: string, block: boolean): Promise<void> {
    try {
      const data: Record<string, any> = {
        entityId: caseId,
        block: block,
      };

      const response: AxiosResponse<void> = await this.api.patch(
        `/api/Cases/updateBlock`,
        data
      );

      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(`Failed to update block info of case with id: ${caseId}`);
    }
  }

  async markNotFoundFileForRetry(
    caseId: string,
    caseFileId: string,
    reason: string
  ): Promise<void> {
    try {
      const data: Record<string, any> = {
        migrationEntityId: caseId,
        fileId: caseFileId,
        reason: reason
      };
      await this.api.post(
        `/api/caseFiles/markNotFoundForRetry`,
        data
      );
    } catch (error) {
      throw new Error(`Failed to mark not found file for retry for caseId: ${caseId} and caseFileId: ${caseFileId}`);
    }
  }

  async getConflictGroups(caseId: string, includeResolved = false): Promise<ConflictGroupDto[]> {
    try {
      const response = await this.api.get(`/api/CaseFiles/GetConflictGroups`, {
        params: { caseId: caseId, includeResolved: includeResolved },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch conflict groups for case with id: ${caseId}`);
    }
  }

  async getConflictReasonCounts(): Promise<ConflictReasonCount[]> {
    try {
      const response = await this.api.get(`/api/CaseFiles/GetConflictReasonCounts`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch conflict reason counts for cases`);
    }
  }

  async resolveConflictGroup(resolutions: ConflictResolutionItemDto[]): Promise<void> {
    try {
      await this.api.post(`/api/CaseFiles/ResolveConflictGroup`, {
        resolutions: resolutions,
      });
    } catch (error) {
      throw apiErrorWithMessages(error, "Failed to resolve conflict group");
    }
  }
}

export default CasesRepository;
